import {memo, type CSSProperties, useState} from 'react'

import styles from '../MonaDivider.module.css'
import {COLUMN_COUNT, getMonaDividerGridCellKey, ROW_COUNT, type MonaDividerGridFocusRange} from './grid-focus'
import gradientImage1 from '../assets/tile-gradient-1.webp'
import gradientImage2 from '../assets/tile-gradient-2.webp'

const ANIMATE_FOCUS_COLORS_ON_ALTERNATE_RECTS_ONLY: boolean = true
const MAX_RECENTLY_UNFOCUSED_CELL_COUNT = 20

const CANDIDATE_COLORS = ['#08872B', '#23EA57', '#FD9FE2', '#C898FD', '#8DD6FF'] as const
const GRADIENT_IMAGES = {
  color1: 'gradient-image-1',
  color2: 'gradient-image-2',
} as const
const WEIGHTED_CANDIDATE_COLORS = [
  CANDIDATE_COLORS[0],
  CANDIDATE_COLORS[0],
  CANDIDATE_COLORS[0],
  CANDIDATE_COLORS[1],
  CANDIDATE_COLORS[1],
  CANDIDATE_COLORS[2],
  CANDIDATE_COLORS[3],
  CANDIDATE_COLORS[4],
  GRADIENT_IMAGES.color1,
  CANDIDATE_COLORS[4],
  GRADIENT_IMAGES.color2,
] as const

type gradientImage = (typeof GRADIENT_IMAGES)[keyof typeof GRADIENT_IMAGES]

type CellColorVariables = CSSProperties & {
  '--delay-color': string
  '--default-color': string
  '--default-opacity': number
  '--focus-color': string
  '--focus-opacity': number
  '--gradient-image'?: string
}

type GridVariables = CSSProperties & {
  '--column-count': number
  '--row-count': number
}

const DEFAULT_COLORS = {
  '--default-color': 'transparent',
  '--default-opacity': 0,
  '--focus-color': 'transparent',
} satisfies Omit<CellColorVariables, '--delay-color' | '--focus-opacity'>

const GRID_VARIABLES = {
  '--column-count': COLUMN_COUNT,
  '--row-count': ROW_COUNT,
} satisfies GridVariables

function detectAlternateState(x: number, y: number) {
  const version1 = y === Math.round(Math.abs(((x / 7) % 2) - 1) * 7)
  const version2 = y - 1 === Math.round(Math.abs(((x / 7) % 2) - 1) * 7)
  const version3 = y === Math.round(Math.abs((((x + 7) / 7) % 2) - 1) * 7)
  const version4 = y === Math.round(Math.abs((((x + 3) / 7) % 2) - 1) * 7)

  return {
    isAlternate: version1 || version2 || version3 || version4,
    isVersion4: version4,
  }
}

function getDefaultOpacity(x: number, y: number, isVersion4: boolean) {
  const min = 0.0
  const max = 0.5
  const value = ((x * 17 + y * 31 + x * y * 7) % 101) / 100
  const opacity = Math.round((min + value * (max - min)) * 100) / 100
  return isVersion4 ? opacity * 0.4 : opacity
}

function getColorDelay(x: number, y: number) {
  const min = 1
  const max = 1.5
  let value = ((x * 37 + y * 61 + x * y * 17) % 101) / 100
  value *= value
  return `${(min + value * (max - min)).toFixed(3)}s`
}

function getCandidateColor(x: number, y: number) {
  const index = (x * 13 + y * 29 + x * y * 7) % WEIGHTED_CANDIDATE_COLORS.length
  return WEIGHTED_CANDIDATE_COLORS[index] ?? CANDIDATE_COLORS[0]
}

function isgradientImage(color: string): color is gradientImage {
  return color === GRADIENT_IMAGES.color1 || color === GRADIENT_IMAGES.color2
}

function createCells() {
  return Array.from({length: COLUMN_COUNT * ROW_COUNT}, (_, index) => {
    const gridX = index % COLUMN_COUNT
    const gridY = Math.floor(index / COLUMN_COUNT)
    const {isAlternate, isVersion4} = detectAlternateState(gridX, gridY)
    const colorDelay = getColorDelay(gridX, gridY)
    const focusOpacity = isAlternate ? 1.0 : 0.7
    const candidateColor = getCandidateColor(gridX, gridY)
    const gradientImage = isgradientImage(candidateColor) ? candidateColor : null
    const defaultColor = isAlternate ? '#8D8D8D' : 'transparent'
    const defaultOpacity = isAlternate ? getDefaultOpacity(gridX, gridY, isVersion4) : 0
    const focusColor = gradientImage ? (isAlternate ? '#8D8D8D' : 'transparent') : candidateColor

    return {
      colors: {
        ...DEFAULT_COLORS,
        '--delay-color': colorDelay,
        '--default-color': defaultColor,
        '--default-opacity': defaultOpacity,
        '--focus-color': focusColor,
        '--focus-opacity': focusOpacity,
      } satisfies CellColorVariables,
      gradientImage,
      isAlternate,
      key: getMonaDividerGridCellKey(gridX, gridY),
      x: gridX,
      y: gridY,
    }
  })
}

const CELLS = createCells()

type Cell = (typeof CELLS)[number]

type CellFocusState = {
  focusedCellKeys: readonly string[]
  recentlyUnfocusedCellKeys: readonly string[]
}

function getFocusedCellKeys(focusedRange: MonaDividerGridFocusRange | null) {
  if (focusedRange === null) return []

  return CELLS.filter(
    cell =>
      (!ANIMATE_FOCUS_COLORS_ON_ALTERNATE_RECTS_ONLY || cell.isAlternate) &&
      cell.x >= focusedRange.firstColumn &&
      cell.x <= focusedRange.lastColumn &&
      cell.y >= focusedRange.firstRow &&
      cell.y <= focusedRange.lastRow &&
      focusedRange.focusedCellKeys.has(cell.key),
  ).map(cell => cell.key)
}

function areCellKeysEqual(first: readonly string[], second: readonly string[]) {
  return first.length === second.length && first.every((key, index) => key === second[index])
}

function getNextCellFocusState(previousState: CellFocusState, focusedCellKeys: readonly string[]): CellFocusState {
  const focusedCellKeySet = new Set(focusedCellKeys)
  const newlyUnfocusedCellKeys = previousState.focusedCellKeys.filter(key => !focusedCellKeySet.has(key))
  const newlyUnfocusedCellKeySet = new Set(newlyUnfocusedCellKeys)

  return {
    focusedCellKeys,
    recentlyUnfocusedCellKeys: [
      ...newlyUnfocusedCellKeys,
      ...previousState.recentlyUnfocusedCellKeys.filter(
        key => !focusedCellKeySet.has(key) && !newlyUnfocusedCellKeySet.has(key),
      ),
    ].slice(0, MAX_RECENTLY_UNFOCUSED_CELL_COUNT),
  }
}

const MonaDividerGridCell = memo(function GridCell({
  cell,
  isFocused,
  GradientImage,
}: {
  cell: Cell
  isFocused: boolean
  GradientImage: string | null
}) {
  const style = {
    ...cell.colors,
    ...(GradientImage ? {'--gradient-image': `url("${GradientImage}")`} : {}),
  } satisfies CellColorVariables

  return <span className={styles.Cell} data-focused={isFocused ? 'true' : undefined} style={style} />
})

export function MonaDividerGrid({focusedRange = null}: {focusedRange?: MonaDividerGridFocusRange | null}) {
  const focusedCellKeys = getFocusedCellKeys(focusedRange)
  const [cellFocusState, setCellFocusState] = useState<CellFocusState>(() => ({
    focusedCellKeys,
    recentlyUnfocusedCellKeys: [],
  }))
  let nextCellFocusState = cellFocusState

  if (!areCellKeysEqual(cellFocusState.focusedCellKeys, focusedCellKeys)) {
    nextCellFocusState = getNextCellFocusState(cellFocusState, focusedCellKeys)
    setCellFocusState(nextCellFocusState)
  }

  const visibleFocusedCellKeys = new Set([
    ...nextCellFocusState.focusedCellKeys,
    ...nextCellFocusState.recentlyUnfocusedCellKeys,
  ])
  const GradientImages = {
    [GRADIENT_IMAGES.color1]: gradientImage1,
    [GRADIENT_IMAGES.color2]: gradientImage2,
  } satisfies Record<gradientImage, string>

  return (
    <div aria-hidden="true" className={styles.Grid} style={GRID_VARIABLES}>
      {CELLS.map(cell => {
        const isFocused = visibleFocusedCellKeys.has(cell.key)

        return (
          <MonaDividerGridCell
            key={cell.key}
            cell={cell}
            isFocused={isFocused}
            GradientImage={cell.gradientImage ? GradientImages[cell.gradientImage] : null}
          />
        )
      })}
    </div>
  )
}
